(() => {
  if (window.RaidageShopLook) {
    window.RaidageShopLook.initAll(document);
    return;
  }

  const instances = new WeakMap();
  const liveInstances = new Set();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ShopLook {
    constructor(section) {
      this.section = section;
      this.slides = section.querySelector('.shop-look__slides');
      this.looks = Array.from(section.querySelectorAll('.shop-look__slide'));
      this.nav = section.querySelector('.shop-look__mobile-nav');
      this.previous = section.querySelector('[data-shop-look-prev]');
      this.next = section.querySelector('[data-shop-look-next]');
      this.currentLabel = section.querySelector('[data-shop-look-current]');
      this.totalLabel = section.querySelector('[data-shop-look-total]');
      this.controller = new AbortController();
      this.activeHotspot = null;
      this.activePopover = null;
      this.pinned = false;
      this.restoringFocus = false;
      this.closeTimer = null;
      this.scrollFrame = null;
      this.bind();
      this.updateNavigation();
    }

    bind() {
      const options = { signal: this.controller.signal };
      this.section.addEventListener('click', (event) => {
        const hotspot = event.target.closest('[data-shop-look-hotspot]');
        if (hotspot && this.section.contains(hotspot)) {
          event.preventDefault();
          this.open(hotspot, true);
          return;
        }
        const close = event.target.closest('[data-shop-look-close]');
        if (close && this.section.contains(close)) {
          event.preventDefault();
          this.close(true);
          return;
        }
        if (event.target.closest('[data-shop-look-prev]')) this.move(-1);
        if (event.target.closest('[data-shop-look-next]')) this.move(1);
      }, options);

      this.section.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'touch') return;
        const hotspot = event.target.closest('[data-shop-look-hotspot]');
        if (hotspot && this.section.contains(hotspot) && !hotspot.contains(event.relatedTarget)) {
          this.open(hotspot, false);
        }
        if (this.activePopover?.contains(event.target)) this.cancelClose();
      }, options);
      this.section.addEventListener('pointerout', (event) => {
        if (event.pointerType === 'touch' || this.pinned) return;
        if (this.activeHotspot?.contains(event.target) && !this.activeHotspot.contains(event.relatedTarget)) {
          this.scheduleClose(this.activePopover);
        }
        if (this.activePopover?.contains(event.target) && !this.activePopover.contains(event.relatedTarget)) {
          this.scheduleClose(this.activePopover);
        }
      }, options);
      this.section.addEventListener('focusin', (event) => {
        if (this.restoringFocus) return;
        const hotspot = event.target.closest('[data-shop-look-hotspot]');
        if (hotspot && this.section.contains(hotspot)) this.open(hotspot, false);
        if (this.activePopover?.contains(event.target)) this.cancelClose();
      }, options);
      this.section.addEventListener('focusout', (event) => {
        if (!this.activePopover) return;
        const next = event.relatedTarget;
        if (this.activePopover.contains(next) || this.activeHotspot.contains(next)) return;
        this.scheduleClose(this.activePopover, 0);
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.activePopover) return;
        if (this.activePopover.contains(event.target) || this.activeHotspot.contains(event.target)) return;
        this.close();
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activePopover) {
          event.preventDefault();
          this.close(true);
        }
      }, options);
      document.addEventListener('raidage:overlay-open', (event) => {
        if (this.activePopover && event.detail?.element !== this.activePopover) this.close();
      }, options);
      this.slides?.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.close();
          this.updateNavigation();
        });
      }, { passive: true, signal: this.controller.signal });
      window.addEventListener('resize', () => {
        this.close();
        this.updateNavigation();
      }, options);
      window.addEventListener('scroll', () => this.close(), { passive: true, signal: this.controller.signal });
      window.visualViewport?.addEventListener('resize', () => {
        if (this.activePopover) this.place();
      }, options);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    scheduleClose(popover, delay = 180) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        if (this.activePopover === popover && !popover.contains(document.activeElement) && document.activeElement !== this.activeHotspot) {
          this.close();
        }
      }, delay);
    }

    open(hotspot, pinned) {
      const popover = this.section.querySelector(`#${CSS.escape(hotspot.getAttribute('aria-controls'))}`);
      if (!popover) return;
      if (this.activePopover === popover) {
        this.pinned = this.pinned || pinned;
        this.cancelClose();
        return;
      }
      this.close();
      this.activeHotspot = hotspot;
      this.activePopover = popover;
      this.pinned = pinned;
      hotspot.setAttribute('aria-expanded', 'true');
      popover.hidden = false;
      this.place();
      document.dispatchEvent(new CustomEvent('raidage:overlay-open', { detail: { source: 'shop-look', element: popover } }));
    }

    place() {
      if (!this.activePopover || !this.activeHotspot) return;
      const card = this.activePopover;
      const anchor = this.activeHotspot.getBoundingClientRect();
      const viewLeft = window.visualViewport?.offsetLeft || 0;
      const viewTop = window.visualViewport?.offsetTop || 0;
      const viewWidth = window.visualViewport?.width || document.documentElement.clientWidth;
      const viewHeight = window.visualViewport?.height || window.innerHeight;
      const viewRight = viewLeft + viewWidth;
      const viewBottom = viewTop + viewHeight;
      const gutter = 12;
      card.style.maxWidth = `${Math.max(1, viewWidth - gutter * 2)}px`;
      card.style.maxHeight = `${Math.max(1, Math.min(420, viewHeight - gutter * 2))}px`;
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      let left = anchor.right + gutter;
      if (left + width > viewRight - gutter) left = anchor.left - width - gutter;
      if (left < viewLeft + gutter) left = anchor.left + anchor.width / 2 - width / 2;
      left = Math.max(viewLeft + gutter, Math.min(left, Math.max(viewLeft + gutter, viewRight - width - gutter)));
      const top = Math.max(viewTop + gutter, Math.min(anchor.top + anchor.height / 2 - height / 2, Math.max(viewTop + gutter, viewBottom - height - gutter)));
      card.style.left = `${Math.round(left)}px`;
      card.style.top = `${Math.round(top)}px`;
    }

    close(restoreFocus = false) {
      this.cancelClose();
      if (!this.activePopover) return;
      const hotspot = this.activeHotspot;
      this.activePopover.hidden = true;
      this.activePopover.style.left = '';
      this.activePopover.style.top = '';
      this.activePopover.style.maxWidth = '';
      this.activePopover.style.maxHeight = '';
      hotspot.setAttribute('aria-expanded', 'false');
      this.activePopover = null;
      this.activeHotspot = null;
      this.pinned = false;
      if (restoreFocus && hotspot.isConnected) {
        this.restoringFocus = true;
        try { hotspot.focus({ preventScroll: true }); }
        finally { this.restoringFocus = false; }
      }
    }

    nearestIndex() {
      if (!this.slides || !this.looks.length) return 0;
      const rail = this.slides.getBoundingClientRect();
      return this.looks.reduce((best, look, index) => {
        const distance = Math.abs(look.getBoundingClientRect().left - rail.left);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    }

    updateNavigation() {
      if (!this.nav) return;
      const count = this.looks.length;
      this.nav.hidden = count < 2;
      if (this.totalLabel) this.totalLabel.textContent = count;
      const index = this.nearestIndex();
      if (this.currentLabel) this.currentLabel.textContent = index + 1;
      if (this.previous) this.previous.disabled = index === 0;
      if (this.next) this.next.disabled = index >= count - 1;
    }

    showLook(look) {
      if (!this.slides || !look) return;
      const rail = this.slides.getBoundingClientRect();
      const target = look.getBoundingClientRect();
      this.slides.scrollTo({
        left: this.slides.scrollLeft + target.left - rail.left,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.close();
    }

    move(direction) {
      const next = Math.max(0, Math.min(this.looks.length - 1, this.nearestIndex() + direction));
      this.showLook(this.looks[next]);
    }

    revealBlock(block) {
      const look = block.closest('.shop-look__slide');
      if (!look || !this.section.contains(look)) return;
      this.showLook(look);
      look.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    destroy() {
      this.close();
      this.cancelClose();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.controller.abort();
      instances.delete(this.section);
      liveInstances.delete(this);
    }
  }

  function initAll(root = document) {
    for (const instance of liveInstances) {
      if (!instance.section.isConnected) instance.destroy();
    }
    const sections = root.matches?.('.shop-look') ? [root] : root.querySelectorAll('.shop-look');
    sections.forEach((section) => {
      if (instances.has(section)) return;
      const instance = new ShopLook(section);
      instances.set(section, instance);
      liveInstances.add(instance);
    });
  }

  function destroyWithin(root) {
    for (const instance of liveInstances) {
      if (root === instance.section || root.contains(instance.section)) instance.destroy();
    }
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.shop-look');
    instances.get(section)?.revealBlock(event.target);
  });
  window.RaidageShopLook = { initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
})();
